/**
 * Rent reminder text and deep links.
 *
 * Nothing here sends a message. The links open the landlord's own WhatsApp or
 * SMS app with the text prefilled; the landlord reviews it and presses send.
 */
import { format } from 'date-fns';
import { formatCurrency } from '../format';
import { daysBetween, toYmd, ymdParts } from './dates';

export interface ReminderInvoice {
  invoiceNumber: string;
  outstanding: number;
  dueDate: string;
}

export interface ReminderInput {
  tenantFirstName: string;
  invoices: ReminderInvoice[];
  /** YYYY-MM-DD, used to word "was due" vs "is due". */
  today: string;
  /** Landlord or organisation name for the sign-off. */
  senderName?: string;
}

export type ReminderChannel = 'whatsapp' | 'sms';

export function formatDueDate(date: string): string {
  const ymd = toYmd(date);
  if (!ymd) return 'an unknown date';
  const [y, m, d] = ymdParts(ymd);
  return format(new Date(y, m - 1, d), 'd MMM yyyy');
}

function closing(senderName?: string): string {
  const thanks =
    'If you have already paid, please ignore this message, and thank you.';
  const name = senderName?.trim();
  return name ? `${thanks}\n\n${name}` : thanks;
}

export function buildReminderMessage({
  tenantFirstName,
  invoices,
  today,
  senderName,
}: ReminderInput): string {
  const name = tenantFirstName.trim();
  const greeting = name ? `Hello ${name},` : 'Hello,';

  const [inv] = invoices;
  if (inv && invoices.length === 1) {
    const overdue = daysBetween(inv.dueDate, today) > 0;
    const when = overdue ? 'was due on' : 'is due on';
    return [
      `${greeting} this is a friendly reminder that ${formatCurrency(inv.outstanding)} is outstanding on invoice ${inv.invoiceNumber}, which ${when} ${formatDueDate(inv.dueDate)}.`,
      `Please use ${inv.invoiceNumber} as the payment reference.`,
      closing(senderName),
    ].join('\n\n');
  }

  const total = invoices.reduce((sum, inv) => sum + inv.outstanding, 0);
  const lines = invoices.map(
    (inv) =>
      `- ${inv.invoiceNumber}: ${formatCurrency(inv.outstanding)}, due ${formatDueDate(inv.dueDate)}`,
  );
  return [
    `${greeting} this is a friendly reminder that ${formatCurrency(total)} is outstanding on your rent invoices:`,
    lines.join('\n'),
    'Please use the invoice number as the payment reference.',
    closing(senderName),
  ].join('\n\n');
}

/** `digits` is the international number without "+", e.g. 2348031234567. */
export function buildWhatsAppUrl(digits: string, text: string): string {
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

/** RFC 5724 sms: URI; `?body=` is understood by Android and current iOS. */
export function buildSmsUrl(digits: string, text: string): string {
  return `sms:+${digits}?body=${encodeURIComponent(text)}`;
}

export function buildReminderUrl(
  channel: ReminderChannel,
  digits: string,
  text: string,
): string {
  return channel === 'whatsapp'
    ? buildWhatsAppUrl(digits, text)
    : buildSmsUrl(digits, text);
}
